// client/src/hooks/useTemplateContrato.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api'; 
import useAlert from './useAlert';

const BASE_ROUTE = 'templates/contrato';
const CONTRATO_TEMPLATES_QUERY_KEY = 'templatesContrato';

// Hook para buscar UMA LISTA de templates de contrato
export const useTemplatesContrato = (params = {}, options = {}) => {
    const { setAlert } = useAlert();
    return useQuery({
        queryKey: [CONTRATO_TEMPLATES_QUERY_KEY, params],
        queryFn: async () => {
            try {
                const { data } = await apiClient.get(`/${BASE_ROUTE}`, { params });
                return data.data || [];
            } catch (error) {
                const errorMsg = error.response?.data?.error || 'Erro ao carregar templates de contrato.';
                setAlert(errorMsg, 'danger');
                throw error;
            }
        },
        staleTime: 1000 * 60 * 10, // 10 minutos
        ...options
    });
};

// Variáveis reconhecidas por populateContractTemplate
// (server/utils/contractUtils.js).
export const useContratoPlaceholders = () => {
    return useQuery({
        queryKey: [CONTRATO_TEMPLATES_QUERY_KEY, 'placeholders'],
        queryFn: async () => {
            const { data } = await apiClient.get(`/${BASE_ROUTE}/placeholders`);
            return data.data || [];
        },
        staleTime: 1000 * 60 * 30,
    });
};

// ✅ NOVO HOOK: Templates ativos por tipo de contrato (para formulários)
export const useTemplatesAtivosPorTipo = (tipoContratoId, enabled = true) => {
    const { setAlert } = useAlert();
    return useQuery({
        queryKey: [CONTRATO_TEMPLATES_QUERY_KEY, 'ativos', tipoContratoId],
        queryFn: async () => {
            if (!tipoContratoId) return [];
            try {
                const { data } = await apiClient.get(`/${BASE_ROUTE}`, {
                    params: { 
                        tipoContrato: tipoContratoId, 
                        ativo: true 
                    }
                });
                return data.data || [];
            } catch (error) {
                const errorMsg = error.response?.data?.error || 'Erro ao carregar templates disponíveis.';
                setAlert(errorMsg, 'danger');
                throw error;
            }
        },
        enabled: !!tipoContratoId && enabled,
        staleTime: 1000 * 60 * 5, // 5 minutos
    });
};

// Hook para buscar UM template de contrato por ID
export const useTemplateContratoById = (id, options = {}) => {
    const { setAlert } = useAlert();
    return useQuery({
        queryKey: [CONTRATO_TEMPLATES_QUERY_KEY, id],
        queryFn: async () => {
            if (!id) return null;
            try {
                const { data } = await apiClient.get(`/${BASE_ROUTE}/${id}`);
                return data.data || data;
            } catch (error) {
                const errorMsg = error.response?.data?.error || 'Erro ao carregar detalhes do template.';
                setAlert(errorMsg, 'danger');
                throw error;
            }
        },
        enabled: !!id, 
        ...options
    });
};

// Hook para operações de mutação
export const useMutateTemplateContrato = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();
    
    const onMutationSuccess = (message) => {
        queryClient.invalidateQueries({ queryKey: [CONTRATO_TEMPLATES_QUERY_KEY] });
        setAlert(message, 'success');
    };

    const addTemplateContrato = useMutation({
        mutationFn: async (templateData) => {
            try {
                const { data } = await apiClient.post(`/${BASE_ROUTE}`, templateData);
                return data;
            } catch (error) {
                const errorMsg = error.response?.data?.error || 'Erro ao criar template.';
                throw new Error(errorMsg);
            }
        },
        onSuccess: (data) => {
            onMutationSuccess(data.message || 'Template de contrato criado com sucesso!');
        },
        onError: (err) => {
            setAlert(err.message || 'Erro ao criar template.', 'danger');
        },
    });

    const updateTemplateContrato = useMutation({
        mutationFn: async ({ id, templateData }) => {
            try {
                const { data } = await apiClient.put(`/${BASE_ROUTE}/${id}`, templateData);
                return data;
            } catch (error) {
                const errorMsg = error.response?.data?.error || 'Erro ao atualizar template.';
                throw new Error(errorMsg);
            }
        },
        onSuccess: (data) => {
            onMutationSuccess(data.message || 'Template de contrato atualizado com sucesso!');
        },
        onError: (err) => {
            setAlert(err.message || 'Erro ao atualizar template.', 'danger');
        },
    });

    const deleteTemplateContrato = useMutation({
        mutationFn: async (id) => {
            if (window.confirm('Tem certeza que deseja apagar este template?')) {
                try {
                    const { data } = await apiClient.delete(`/${BASE_ROUTE}/${id}`);
                    return data;
                } catch (error) {
                    const errorMsg = error.response?.data?.error || 'Erro ao deletar template.';
                    throw new Error(errorMsg);
                }
            }
            return Promise.reject(new Error('Ação cancelada'));
        },
        onSuccess: (data) => {
            onMutationSuccess(data.message || 'Template de contrato removido com sucesso!');
        },
        onError: (err) => {
            if (err.message !== 'Ação cancelada') {
                setAlert(err.message || 'Erro ao deletar template.', 'danger');
            }
        },
    });

    const importTemplateContrato = useMutation({
        mutationFn: async (file) => {
            const formData = new FormData();
            formData.append("file", file);
            try {
                const { data } = await apiClient.post(`/${BASE_ROUTE}/import`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                return data;
            } catch (error) {
                const errorMsg = error.response?.data?.error || 'Erro ao importar template.';
                throw new Error(errorMsg);
            }
        },
        onSuccess: (data) => {
            onMutationSuccess(data.message || 'Template de contrato importado com sucesso!');
        },
        onError: (err) => {
            setAlert(err.message || 'Erro ao importar template.', 'danger');
        },
    });

    const exportTemplateContrato = async (id) => {
        try {
            const response = await apiClient.get(`/${BASE_ROUTE}/${id}/export`, { 
                responseType: "blob" 
            });
            
            // ✅ CORREÇÃO: Obtém o nome do arquivo do header ou usa um padrão
            const contentDisposition = response.headers['content-disposition'];
            let filename = `contrato-template-${id}.json`;
            
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }
            
            const blob = new Blob([response.data], { type: "application/json" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            setAlert('Template exportado com sucesso!', 'success');
        } catch (err) {
            const errorMsg = err.response?.data?.error || 'Erro ao exportar template de contrato.';
            setAlert(errorMsg, 'danger');
        }
    };

    return { 
        addTemplateContrato, 
        updateTemplateContrato, 
        deleteTemplateContrato,
        importTemplateContrato,
        exportTemplateContrato,
        isPendingContrato: addTemplateContrato.isPending || updateTemplateContrato.isPending || deleteTemplateContrato.isPending
    };
};